import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Select, MenuItem, FormControl, InputLabel, Paper, useTheme, alpha, Stack, Alert, Snackbar } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { TeamsInterface, TeamsStatus } from 'ts-interfaces';
import { addDoc, collection, getDocs, query, where, Timestamp, doc, updateDoc } from 'firebase/firestore';
import { firebaseHelper } from 'ui';
import { v4 as uuidv4 } from 'uuid';
import { CustomSnackbar } from './SnackbarComponent';

interface InviteProps {
  designFirmId: string;
  designFirmDomain: string;
  currentSeatsUsed: number;
  totalSeatsAvailable: number;
}

// Added type to track archived user data
interface ArchivedUserData {
  found: boolean;
  docId?: string;
  userData?: any;
}

const Invite: React.FC<InviteProps> = ({ 
  designFirmId, 
  designFirmDomain, 
  currentSeatsUsed, 
  totalSeatsAvailable 
}) => {
  const theme = useTheme();
  const [email, setEmail] = useState<string>('');
  const [role, setRole] = useState<TeamsInterface['role']>('designer');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

  const validateEmail = (email: string): boolean => {
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }

    // Check if email domain matches design firm domain
    const emailDomain = email.split('@')[1];
    if (emailDomain !== designFirmDomain) {
      setError(`Email must be from the ${designFirmDomain} domain`);
      return false;
    }

    return true;
  };

  const checkSeatsAvailability = (selectedRole: TeamsInterface['role']): boolean => {
    // Admin-only role doesn't count against seat limits
    if (selectedRole !== 'admin' && currentSeatsUsed >= totalSeatsAvailable) {
      setError(`No seats available. Your plan allows ${totalSeatsAvailable} team members.`);
      return false;
    }
    return true;
  };

  const checkDuplicateEmail = async (email: string): Promise<ArchivedUserData> => {
    try {
      const teamsRef = collection(firebaseHelper.db, 'teams');
      const q = query(
        teamsRef, 
        where('email', '==', email),
        where('designFirmID', '==', designFirmId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Check if the user is archived
        const docData = querySnapshot.docs[0].data();
        if (docData.status === TeamsStatus.ARCHIVED) {
          // Return the archived user's data to be updated
          return {
            found: true,
            docId: querySnapshot.docs[0].id,
            userData: docData
          };
        }
        
        // User exists but not archived
        setError('This email is already registered or invited');
        return { found: true };
      }
      
      // No existing user found
      return { found: false };
    } catch (error) {
      console.error("Error checking for duplicate email:", error);
      setError('An error occurred while checking email availability');
      return { found: true }; // Treat as found to prevent further processing
    }
  };

  const generateInviteKey = (): string => {
    return uuidv4();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Check if email is empty
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(email)) return;
    if (!checkSeatsAvailability(role)) return;

    setLoading(true);
    
    try {
      // Check for duplicates or archived users
      const emailCheck = await checkDuplicateEmail(email);
      
      if (emailCheck.found) {
        // If the user is archived, update their status to invited
        if (emailCheck.docId && emailCheck.userData?.status === TeamsStatus.ARCHIVED) {
          // Generate new invite key
          const inviteKey = generateInviteKey();
          
          // Update the archived user to invited status
          const userDocRef = doc(firebaseHelper.db, 'teams', emailCheck.docId);
          await updateDoc(userDocRef, {
            status: TeamsStatus.INVITED,
            role: role, // Update with new role
            inviteKey: inviteKey,
            inviteSent: false,
            updatedAt: Date.now()
          });
          
          // Reset form and show success message
          setEmail('');
          setRole('designer');
          setSuccess(true);
          setSnackbarOpen(true);
        } else {
          // User exists and not archived
          setLoading(false);
          return;
        }
      } else {
        // No existing user - create new team member record
        const inviteKey = generateInviteKey();
        
        const newTeamMember: Omit<TeamsInterface, 'id'> = {
          designFirmID: designFirmId,
          email: email.toLowerCase(),
          inviteKey,
          role,
          status: TeamsStatus.INVITED,
          inviteSent: false,
          createdAt: Date.now(),
          updatedAt: Date.now()
        };

        // Add to Firestore
        const teamsRef = collection(firebaseHelper.db, 'teams');
        await addDoc(teamsRef, newTeamMember);

        // Reset form and show success message
        setEmail('');
        setRole('designer');
        setSuccess(true);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error inviting team member:", error);
      setError('An error occurred while sending the invitation');
    } finally {
      setLoading(false);
    }
  };

  const maxSeats = (currentSeatsUsed >= totalSeatsAvailable) && role !== 'admin'
  return (
    <Paper sx={{
      p: 3,
      bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : alpha(theme.palette.background.paper, 0.9),
      borderRadius: 2,
      width: '95%',
      borderColor:'white' 
    }}>
      <form onSubmit={handleSubmit}>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" gap={1}>
            <PersonAddIcon />
            <Typography variant="h6" fontWeight="medium">
              Invite New Team Member
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ paddingTop:'2px' }}>
            Seats used: {currentSeatsUsed} of {totalSeatsAvailable}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
          )}
          
          {success && (
            <Alert 
              icon={<CheckCircleOutlineIcon fontSize="inherit" />}
              severity="success" 
              sx={{ 
                mt: 1,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" fontWeight="medium">
                  Invitation sent successfully!
                </Typography>
                <Button 
                  size="small" 
                  variant="text" 
                  color="success" 
                  onClick={() => setSuccess(false)}
                >
                  Send another
                </Button>
              </Stack>
            </Alert>
          )}
          
          {!success && (
            <Stack direction={"row"} spacing={2} sx={{paddingTop:'10px', mb: 1}}>
              <TextField
                size='small'          
                label="Email"              
                placeholder=  { designFirmDomain ? "user@"+designFirmDomain : "user@email.com" }
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!error && error.includes('email')}
                InputLabelProps={{
                  shrink: true,
                  sx: { color: theme.palette.primary.main }
                }}
                sx={{
                  width:'60%',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: theme.palette.divider,
                    },
                  },
                }}
              />

              <FormControl sx={{width:'20%'}} variant="outlined">
                <InputLabel id="role-label">Role</InputLabel>
                <Select     
                  size='small'
                  labelId="role-label"
                  value={role}
                  label="Role"
                  onChange={(e) => setRole(e.target.value as TeamsInterface['role'])}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.divider,
                    },
                  }}
                >
                  <MenuItem value="designer">Designer</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="designer_and_admin">Designer & Admin</MenuItem>
                </Select>
              </FormControl>

              <Button
                size='small'
                variant="contained"
                color="primary"              
                startIcon={<PersonAddIcon />}
                type="submit"
                disabled={loading || maxSeats}
                sx={{ height:'40px', width:'20%', py: 1.5 }}
              >
                {loading ? 'Sending...' : 'Invite'}
              </Button>
            </Stack>
          )}

          {success ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
              A notification has been sent to the provided email address with instructions to join.
            </Typography>
          ) : (
            <Stack direction="row" alignItems="center" gap={1}>
              <InfoIcon color="info" sx={{ mt: 0.3 }} />
              {maxSeats!==true && 
                <Typography variant="body2">                
                  <Box component="span" fontWeight="medium">Tip:</Box> Start by inviting key team members who will be collaborating on design projects.
                  </Typography>
              }
              {maxSeats === true && 
                <Typography variant="body2">
                  <Box component="span" fontWeight="medium">Note:</Box> You have inivited the maximum amount of users. 
                </Typography>
              }
            </Stack>
          )}
        </Stack>
      </form>

      <CustomSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message="Invitation sent successfully!"
      />

    </Paper>
  );
};

export default Invite;